import { negate } from 'lodash-es'

import { getCoverLevelConfig, ProductConfig } from '../state/configuration'
import { ConditionalCover, CoverLevelOption, CoverOption } from './types'

export const isAddOn = (
  product: ProductConfig,
  coverLevelOption: CoverLevelOption | undefined,
) => negate(isAuxiliary(product, coverLevelOption))

export const isAuxiliary =
  (product: ProductConfig, coverLevelOption: CoverLevelOption | undefined) =>
  (coverOption: CoverOption) => {
    const { covers } = getCoverLevelConfig(product, coverLevelOption?.coverLevel) ?? {}

    return Boolean(
      covers?.optional?.find((it) => it.code === coverOption.coverOption)?.form,
    )
  }

export type ConditionalCoverChange = {
  type: 'ADDED' | 'REMOVED'
  cover: ConditionalCover
}

export const getConditionalCoverChange = (
  current: readonly ConditionalCover[],
  updated: readonly ConditionalCover[],
): ConditionalCoverChange[] => {
  const added = updated
    .filter(
      ({ active, coverCode }) =>
        active && !current.find((it) => it.coverCode === coverCode)?.active,
    )
    .map<ConditionalCoverChange>((cover) => ({
      type: 'ADDED',
      cover,
    }))

  const removed = current
    .filter(
      ({ active, coverCode }) =>
        active && !updated.find((it) => it.coverCode === coverCode)?.active,
    )
    .map<ConditionalCoverChange>((cover) => ({
      type: 'REMOVED',
      cover,
    }))

  return [...added, ...removed]
}

export type CoverOptionChange = {
  type: 'ADDED' | 'REMOVED' | 'AVAILABLE'
  cover: CoverOption
}

export const getCoverOptionChange = (
  current: readonly CoverOption[],
  updated: readonly CoverOption[],
): CoverOptionChange[] => {
  const added = updated
    .filter(
      ({ active, coverOption }) =>
        active && !current.find((it) => it.coverOption === coverOption)?.active,
    )
    .map<CoverOptionChange>((cover) => ({
      type: 'ADDED',
      cover,
    }))

  const removed = current
    .filter(
      ({ active, coverOption }) =>
        active && !updated.find((it) => it.coverOption === coverOption)?.active,
    )
    .map<CoverOptionChange>((cover) => ({
      type: 'REMOVED',
      cover,
    }))

  const available = updated
    .filter(
      ({ active, coverOption, available }) =>
        available &&
        !active &&
        !current.find((it) => it.coverOption === coverOption)?.available,
    )
    .map<CoverOptionChange>((cover) => ({
      type: 'AVAILABLE',
      cover,
    }))

  return [...added, ...removed, ...available]
}
